import React from "react";
import {Button, styled} from "@reside/ui";

import {DocumentTitle} from "../../atoms/document-title";
import {AdminLayout} from "../../atoms/admin-layout";
import {BackdropSpinner} from "../../atoms/spinner";

import {useContentCommits} from "./hooks/useContentCommits";
import {useContentHistory} from "./hooks/useContentHistory";
import {ErrorSummaryBanner} from "./atoms/ErrorSummaryBanner";
import {CommitHistoryTable} from "./atoms/CommitHistoryTable";
import {HistoryTable} from "./atoms/HistoryTable";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #203c63;
  margin: 0;
`;

const ErrorBanner = styled.div`
  background-color: #fff5f5;
  border-left: 4px solid #ff3a45;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  font-size: 13px;
  color: #a10009;
`;

export const PageAdminContent = (): JSX.Element => {
  const {
    data: commits,
    isLoading: commitsLoading,
    isError: commitsError,
    refetch: refetchCommits,
  } = useContentCommits();

  const {
    data: history,
    isLoading: historyLoading,
    isError: historyError,
    refetch: refetchHistory,
  } = useContentHistory();

  const isLoading = commitsLoading || historyLoading;
  const latestCommit = commits?.[0] ?? null;

  const handleRefresh = () => {
    refetchCommits();
    refetchHistory();
  };

  return (
    <BackdropSpinner active={isLoading}>
      <DocumentTitle title="Reside - Admin - Content" />
      <AdminLayout>
        <PageHeader>
          <Title>Content Processing</Title>
          <Button
            onClick={handleRefresh}
            style={{width: "fit-content"}}
          >
            Refresh
          </Button>
        </PageHeader>

        {commitsError && (
          <ErrorBanner>
            Failed to load commit data. The /template-commits endpoint may not
            be available.
          </ErrorBanner>
        )}
        {historyError && (
          <ErrorBanner>
            Failed to load processing history. The /template-history endpoint
            may not be available.
          </ErrorBanner>
        )}

        {latestCommit && <ErrorSummaryBanner latestCommit={latestCommit} />}
        <CommitHistoryTable commits={commits ?? []} />
        <HistoryTable history={history ?? []} />
      </AdminLayout>
    </BackdropSpinner>
  );
};
